import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { BadgeCheck, ChevronLeft, ChevronRight, Contact, Search, X } from "lucide-react";
import { useState } from "react";
import type { Paginated } from "@rn/contracts";
import { PageBody, PageHeader } from "@/components/crm/AppShell";
import { Avatar } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardFooter } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Hint } from "@/components/ui/tooltip";
import { api } from "@/lib/api";
import { formatCurrency, formatPhone, relativeTime } from "@/lib/crm";

export const Route = createFileRoute("/_authenticated/contatos/")({
  component: ContatosPage,
});

type Contact = {
  id: string;
  fullName: string | null;
  phone: string | null;
  phoneNormalized: string | null;
  email: string | null;
  city: string | null;
  state: string | null;
  isCustomer: boolean;
  lifetimeValue: number;
  createdAt: string;
  leadCount: number;
  lastLeadAt: string | null;
};

/**
 * Contatos.
 *
 * A diferença para a lista de leads é o motivo desta tela existir: Maria que
 * preencheu três formulários é UMA linha aqui e três lá. A coluna "captações"
 * mostra exatamente isso, e é o número que revela quem já voltou.
 *
 * Não há seletor de período: a pergunta aqui é "quem está na base", e um
 * contato não deixa de existir porque a última captação foi no mês passado.
 */
function ContatosPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [applied, setApplied] = useState("");
  const [customer, setCustomer] = useState("");
  const [page, setPage] = useState(1);

  const contacts = useQuery({
    queryKey: ["contacts", applied, customer, page],
    queryFn: () =>
      api.get<Paginated<Contact>>("/contacts", {
        search: applied || undefined,
        isCustomer: customer || undefined,
        page,
        pageSize: 25,
      }),
    // Mantém a página anterior enquanto a nova chega: sem isso a tabela pisca
    // para vazio a cada troca de página.
    placeholderData: keepPreviousData,
  });

  const rows = contacts.data?.data ?? [];
  const pagination = contacts.data?.pagination;

  const limparBusca = () => {
    setSearch("");
    setApplied("");
    setPage(1);
  };

  const abrir = (contactId: string) =>
    navigate({ to: "/contatos/$contactId", params: { contactId } });

  return (
    <>
      <PageHeader
        title="Contatos"
        description={
          pagination
            ? `${pagination.total.toLocaleString("pt-BR")} ${pagination.total === 1 ? "pessoa" : "pessoas"} na base — uma linha por pessoa, não por captação`
            : "As pessoas, não as captações"
        }
      >
        {/* A busca só vai ao servidor ao enviar: é uma busca na base inteira,
            não um filtro do que já está na tela. */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setApplied(search.trim());
            setPage(1);
          }}
          className="flex min-w-0 flex-1 flex-wrap items-center gap-2"
        >
          <div className="relative min-w-[12rem] flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Nome, telefone ou e-mail"
              aria-label="Buscar contato"
              className="pl-8 pr-8"
            />
            {search && (
              <button
                type="button"
                aria-label="Limpar busca"
                onClick={limparBusca}
                className="absolute right-1.5 top-1/2 inline-flex size-6 -translate-y-1/2 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <X className="size-3.5" />
              </button>
            )}
          </div>
          <Select
            value={customer}
            onChange={(e) => {
              setCustomer(e.target.value);
              setPage(1);
            }}
            aria-label="Situação"
            className="w-full sm:w-48"
          >
            <option value="">Todos</option>
            <option value="true">Clientes</option>
            <option value="false">Ainda não compraram</option>
          </Select>
          <Button type="submit" variant="secondary">
            Buscar
          </Button>
          {contacts.isFetching && !contacts.isLoading && (
            <span className="text-xs text-muted-foreground" aria-live="polite">
              atualizando…
            </span>
          )}
        </form>
      </PageHeader>

      <PageBody>
        <Card className="overflow-hidden">
          {contacts.isLoading ? (
            <div className="divide-y" aria-busy>
              {Array.from({ length: 8 }, (_, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-3">
                  <Skeleton className="size-9 rounded-full" />
                  <div className="flex-1 space-y-1.5">
                    <Skeleton className="h-3.5 w-40 max-w-[60%]" />
                    <Skeleton className="h-3 w-52 max-w-[80%]" />
                  </div>
                  <Skeleton className="hidden h-3.5 w-10 md:block" />
                  <Skeleton className="hidden h-3.5 w-20 md:block" />
                </div>
              ))}
            </div>
          ) : rows.length === 0 ? (
            applied ? (
              <EmptyState
                icon={Search}
                title="Ninguém encontrado com esse termo."
                description={`Nenhuma pessoa combina com "${applied}". Tente parte do nome ou o telefone só com números.`}
                action={
                  <Button variant="outline" size="sm" onClick={limparBusca}>
                    Limpar busca
                  </Button>
                }
              />
            ) : customer ? (
              <EmptyState
                icon={Contact}
                title={customer === "true" ? "Nenhum cliente ainda." : "Todo mundo já comprou."}
                description={
                  customer === "true"
                    ? "Alguém vira cliente ao ter a primeira venda paga registrada."
                    : "Não há contato na base sem venda registrada."
                }
                action={
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setCustomer("");
                      setPage(1);
                    }}
                  >
                    Ver todos
                  </Button>
                }
              />
            ) : (
              <EmptyState
                icon={Contact}
                title="Nenhum contato ainda."
                description="Cada pessoa entra aqui na primeira captação — pelo webhook ou pelo cadastro manual em Leads."
                action={
                  <Button asChild variant="outline" size="sm">
                    <Link to="/leads">Ir para Leads</Link>
                  </Button>
                }
              />
            )
          ) : (
            <>
              {/* Tabela a partir de md; abaixo disso a mesma lista vira cartões. */}
              <div className="hidden overflow-x-auto md:block">
                <table className="w-full text-sm">
                  <thead className="bg-muted/60 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="px-4 py-2.5">Pessoa</th>
                      <th className="px-4 py-2.5 text-right">Captações</th>
                      <th className="px-4 py-2.5">Última</th>
                      <th className="px-4 py-2.5 text-right">Valor gerado</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {rows.map((contact) => (
                      <tr
                        key={contact.id}
                        // A linha inteira abre a ficha; o nome continua um
                        // link de verdade para teclado e botão do meio.
                        onClick={(e) => {
                          if ((e.target as HTMLElement).closest("a, button")) return;
                          void abrir(contact.id);
                        }}
                        className="cursor-pointer transition-colors hover:bg-muted/40"
                      >
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-3">
                            <Avatar
                              name={contact.fullName}
                              seed={contact.phoneNormalized ?? contact.id}
                              size="md"
                            />
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <Link
                                  to="/contatos/$contactId"
                                  params={{ contactId: contact.id }}
                                  className="truncate font-semibold hover:text-primary"
                                >
                                  {contact.fullName ?? "Sem nome"}
                                </Link>
                                {contact.isCustomer && (
                                  <Badge variant="success">
                                    <BadgeCheck />
                                    Cliente
                                  </Badge>
                                )}
                              </div>
                              <p className="truncate text-xs text-muted-foreground">
                                <span className="tabular-nums">
                                  {formatPhone(contact.phoneNormalized)}
                                </span>
                                {contact.email ? ` · ${contact.email}` : ""}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-2.5 text-right tabular-nums">{contact.leadCount}</td>
                        <td className="whitespace-nowrap px-4 py-2.5 tabular-nums text-muted-foreground">
                          {contact.lastLeadAt ? relativeTime(contact.lastLeadAt) : "—"}
                        </td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-right tabular-nums">
                          {contact.lifetimeValue > 0 ? (
                            <span className="font-medium">
                              {formatCurrency(contact.lifetimeValue)}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <ul className="divide-y md:hidden">
                {rows.map((contact) => (
                  <li key={contact.id}>
                    <Link
                      to="/contatos/$contactId"
                      params={{ contactId: contact.id }}
                      className="flex gap-3 px-4 py-3 transition-colors hover:bg-muted/40 active:bg-muted/60"
                    >
                      <Avatar
                        name={contact.fullName}
                        seed={contact.phoneNormalized ?? contact.id}
                        size="md"
                        className="mt-0.5"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <p className="truncate text-sm font-semibold">
                            {contact.fullName ?? "Sem nome"}
                          </p>
                          {contact.isCustomer && (
                            <Badge variant="success">
                              <BadgeCheck />
                              Cliente
                            </Badge>
                          )}
                        </div>
                        <p className="truncate text-xs text-muted-foreground">
                          <span className="tabular-nums">
                            {formatPhone(contact.phoneNormalized)}
                          </span>
                          {contact.email ? ` · ${contact.email}` : ""}
                        </p>
                        <p className="mt-1.5 flex flex-wrap items-center gap-x-1 text-xs tabular-nums text-muted-foreground">
                          <span>
                            {contact.leadCount} {contact.leadCount === 1 ? "captação" : "captações"}
                          </span>
                          {contact.lastLeadAt && (
                            <>
                              <span className="text-border">·</span>
                              <span>última {relativeTime(contact.lastLeadAt)}</span>
                            </>
                          )}
                          {contact.lifetimeValue > 0 && (
                            <>
                              <span className="text-border">·</span>
                              <span className="font-medium text-foreground">
                                {formatCurrency(contact.lifetimeValue)}
                              </span>
                            </>
                          )}
                        </p>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          )}

          {pagination && pagination.totalPages > 1 && (
            <CardFooter className="justify-between">
              <span className="tabular-nums">
                Página {pagination.page} de {pagination.totalPages}
              </span>
              <div className="flex items-center gap-1">
                <Hint label="Página anterior">
                  <Button
                    variant="outline"
                    size="icon-sm"
                    aria-label="Página anterior"
                    disabled={pagination.page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    <ChevronLeft />
                  </Button>
                </Hint>
                <Hint label="Próxima página">
                  <Button
                    variant="outline"
                    size="icon-sm"
                    aria-label="Próxima página"
                    disabled={pagination.page >= pagination.totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    <ChevronRight />
                  </Button>
                </Hint>
              </div>
            </CardFooter>
          )}
        </Card>
      </PageBody>
    </>
  );
}
